import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';

// login 상태 타입 정의
interface LoginState {
    value: boolean;
}

// unlock 상태 타입 정의
interface UnlockState {
    value: boolean;
}

// MyInfo 상태 타입 정의 (예시로 string 배열로 가정)
type MyInfoState = string[];

// Darkmode 상태 타입 정의
interface DarkmodeState {
    value: boolean;
}

// Languages 상태 타입 정의
interface LanguagesState {
    value: string;
}

// Pincode 상태 타입 정의
interface PincodeState {
    value: string;
}

// UnlockPincode 상태 타입 정의
interface UnlockPincodeState {
    value: string;
}

// Mnemonic 상태 타입 정의
interface MnemonicState {
    value: string;
}

// BlockChain 상태 타입 정의
interface BlockChainState {
    value: string;
}

// HeaderText 상태 타입 정의
interface HeaderText {
    link: string;
    title: string;
}

// qss Password 상태 타입 정의
interface QssPassword {
    value: string;
}

// qss 복구코드 상태 타입 정의
interface QssMnemonic {
    value: string;
}

// qss 복구코드 이름 상태 타입 정의
interface QssMnemonicName {
    value: string;
}

// 백버튼 특정 페이지 지정
interface BackTargetPage {
    value: string;
}

// 선택한 QSS 니모닉 인덱스
interface SelectQssIndex {
    value: number;
}

// 선택한 코인/토큰 탭
interface SelectActiveTab {
    value: string;
}

// qpin 상태 타입 정의
interface QpinState {
    walletAddress: string | null;
    connected: boolean;
    generated: any | null;
    confirmed: any | null;
    buyPending: any | null;   // type: 'buy-pending'
    sell: any | null;         // type: 'sell'
    buyConfirm: any | null;   // type: 'buy-confirm'
}

const initialQpinState: QpinState = {
    walletAddress: null,
    connected: false,
    generated: null,
    confirmed: null,
    buyPending: null,
    sell: null,
    buyConfirm: null,
};

// login 슬라이스
let login = createSlice({
    name: 'login',
    initialState: { value: false } as LoginState,
    reducers: {
        checkLogin(state, action: PayloadAction<boolean>) {
            state.value = action.payload;
        },
    },
});

// unlock 슬라이스
let unlock = createSlice({
    name: 'unlock',
    initialState: { value: false } as UnlockState,
    reducers: {
        checkUnlock(state, action: PayloadAction<boolean>) {
            state.value = action.payload;
        },
    },
});

// MyInfo 슬라이스
let MyInfo = createSlice({
    name: 'MyInfo',
    initialState: [] as MyInfoState,
    reducers: {
        checkMyInfo(state, action: PayloadAction<string[]>) {
            return action.payload;
        },
    },
});

// Darkmode 슬라이스
let Darkmode = createSlice({
    name: 'Darkmode',
    initialState: { value: false } as DarkmodeState,
    reducers: {
        changeDark(state, action: PayloadAction<boolean>) {
            localStorage.setItem('darkMode', action.payload ? 'light' : 'dark');
            state.value = action.payload;
        },
    },
});

// Languages 슬라이스
let Languages = createSlice({
    name: 'Languages',
    initialState: { value: 'en-US' } as LanguagesState,
    reducers: {
        changeLanguages(state, action: PayloadAction<string>) {
            localStorage.setItem('language', action.payload);
            state.value = action.payload;
        },
    },
});

// pincode 슬라이스
let pincode = createSlice({
    name: 'pincode',
    initialState: { value: '' } as PincodeState,
    reducers: {
        checkPincode(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// unlockPincode 슬라이스
let unlockPincode = createSlice({
    name: 'unlockPincode',
    initialState: { value: '' } as UnlockPincodeState,
    reducers: {
        checkUnlockPincode(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// mnemonic 슬라이스
let mnemonic = createSlice({
    name: 'mnemonic',
    initialState: { value: '' } as MnemonicState,
    reducers: {
        checkMnemonic(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// blockChain 슬라이스
let blockChain = createSlice({
    name: 'blockChain',
    initialState: { value: '' } as BlockChainState,
    reducers: {
        checkBlockChain(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// headerText 슬라이스
let headerText = createSlice({
    name: 'headerText',
    initialState: { link: '', title: '' } as HeaderText,
    reducers: {
        checkHeaderText(state, action: PayloadAction<HeaderText>) {
            state.link = action.payload.link;
            state.title = action.payload.title;
        },
    },
});

// qss password 슬라이스
let qssPassword = createSlice({
    name: 'qssPassword',
    initialState: { value: '' } as QssPassword,
    reducers: {
        checkQssPassword(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// qss 복구코드 슬라이스
let qssMnemonic = createSlice({
    name: 'qssMnemonic',
    initialState: { value: '' } as QssMnemonic,
    reducers: {
        checkQssMnemonic(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// qss 복구코드 이름 슬라이스
let qssMnemonicName = createSlice({
    name: 'qssMnemonicName',
    initialState: { value: '' } as QssMnemonicName,
    reducers: {
        checkQssMnemonicName(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// 백버튼 타겟 페이지
let backTargetPage = createSlice({
    name: 'backTargetPage',
    initialState: { value: '' } as BackTargetPage,
    reducers: {
        checkBackTargetPage(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// 선택한 QSS 니모닉 인덱스
let selectQssIndex = createSlice({
    name: 'selectQssIndex',
    initialState: { value: 0 } as SelectQssIndex,
    reducers: {
        checkSelectQssIndex(state, action: PayloadAction<number>) {
            state.value = action.payload;
        },
    },
});

// 선택한 코인/토큰 탭
let selectActiveTab = createSlice({
    name: 'selectActiveTab',
    initialState: { value: '' } as SelectActiveTab,
    reducers: {
        checkSelectActiveTab(state, action: PayloadAction<string>) {
            state.value = action.payload;
        },
    },
});

// qpin 슬라이스
const qpinSlice = createSlice({
    name: 'qpin',
    initialState: initialQpinState,
    reducers: {
        setWalletAddress(state, action: PayloadAction<string>) {
            state.walletAddress = action.payload;
        },
        setConnected(state, action: PayloadAction<boolean>) {
            state.connected = action.payload;
        },
        // 통합값(필요하면 계속 사용)
        setQpinGenerated(state, action: PayloadAction<any>) {
            state.generated = action.payload;
        },
        setQpinConfirmed(state, action: PayloadAction<any>) {
            state.confirmed = action.payload;
        },
        // 타입별 값
        setQpinBuyPending(state, action: PayloadAction<any>) {
            state.buyPending = action.payload;
        },
        setQpinSell(state, action: PayloadAction<any>) {
            state.sell = action.payload;
        },
        setQpinBuyConfirm(state, action: PayloadAction<any>) {
            state.buyConfirm = action.payload;
        },
    },
});

export const {
    setWalletAddress,
    setConnected,
    setQpinGenerated,
    setQpinConfirmed,
    setQpinBuyPending,
    setQpinSell,
    setQpinBuyConfirm,
} = qpinSlice.actions;

// 액션 export
export let { checkLogin } = login.actions;
export let { checkUnlock } = unlock.actions;
export let { checkMyInfo } = MyInfo.actions;
export let { changeDark } = Darkmode.actions;
export let { changeLanguages } = Languages.actions;
export let { checkPincode } = pincode.actions;
export let { checkUnlockPincode } = unlockPincode.actions;
export let { checkMnemonic } = mnemonic.actions;
export let { checkBlockChain } = blockChain.actions;
export let { checkHeaderText } = headerText.actions;
export let { checkQssPassword } = qssPassword.actions;
export let { checkQssMnemonic } = qssMnemonic.actions;
export let { checkQssMnemonicName } = qssMnemonicName.actions;
export let { checkBackTargetPage } = backTargetPage.actions;
export let { checkSelectQssIndex } = selectQssIndex.actions;
export let { checkSelectActiveTab } = selectActiveTab.actions;

// 스토어 설정 및 타입 정의
const store = configureStore({
    reducer: {
        login: login.reducer,
        unlock: unlock.reducer,
        MyInfo: MyInfo.reducer,
        Darkmode: Darkmode.reducer,
        Languages: Languages.reducer,
        pincode: pincode.reducer,
        unlockPincode: unlockPincode.reducer,
        mnemonic: mnemonic.reducer,
        BlockChain: blockChain.reducer,
        headerText: headerText.reducer,
        qssPassword: qssPassword.reducer,
        qssMnemonic: qssMnemonic.reducer,
        qssMnemonicName: qssMnemonicName.reducer,
        backTargetPage: backTargetPage.reducer,
        selectQssIndex: selectQssIndex.reducer,
        selectActiveTab: selectActiveTab.reducer,
        qpin: qpinSlice.reducer, // ✅ qpin 리듀서 추가
    },
});

// RootState와 AppDispatch 타입 정의
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
